import { ReactNode, ReactElement, useEffect, useRef } from 'react';
import {
  useLoadingState,
  LoadingState,
  LoadingStage,
  compat,
  LoadingStateCompat,
  ILoadingStateCompatError,
} from 'app/containers/state/LoadingState';
import { useShallowMemo, useLatestCallback, useLatest } from 'app/utils/react';
import { CancelToken } from 'app/utils/cancelToken';

export interface IAutoLoaderParams<LoadType extends LoadFunctionType> {
  args: Parameters<LoadType>[0];
  load: LoadType;
}

export type IReloadParams<LoadType extends (args: any, ...restArgs: any[]) => any> = Partial<Parameters<LoadType>[0]>;

interface IAutoLoaderNoParams<LoadType extends LoadFunctionNoParamsType> {
  args: unknown;
  load: LoadType;
}

export type LoadFunctionType = (args: any, cancelToken: CancelToken) => PromiseLike<unknown>;

export type LoadFunctionNoParamsType = () => PromiseLike<unknown>;

type PromiseType<T> = T extends PromiseLike<infer U> ? U : never;

export type LoadValueType<LoadType extends (...args: any[]) => any> = PromiseType<ReturnType<LoadType>>;

export function useAutoLoader<LoadType extends LoadFunctionType>(
  params: IAutoLoaderParams<LoadType>,
): LoadingState<LoadValueType<LoadType>> & {
  reload(args?: IReloadParams<LoadType>): void;
  clear(): void;
};

// If the normal types don't work, then fall back to using a version of the
// types that allow you to not specify a type for the args.
export function useAutoLoader<LoadNoParamType extends LoadFunctionNoParamsType>(
  params: IAutoLoaderNoParams<LoadNoParamType>,
): LoadingState<LoadValueType<LoadNoParamType>> & {
  reload: () => void;
  clear: () => void;
};

/**
 * @deprecated Use makeCachedLoader instead
 */
export function useAutoLoader(
  params: IAutoLoaderParams<LoadFunctionType>,
): unknown & {
  reload(args?: IReloadParams<LoadFunctionType>): void;
  clear(): void;
} {
  const { load: latestLoad, clear: latestClear, ...loadingState } = useLoadingState(params);

  const memoizedArgs = useShallowMemo(params.args, { deepCheck: true });
  const reload: {
    (args?: IReloadParams<LoadFunctionType>): void;
  } = useLatestCallback((args) => {
    latestLoad({ ...memoizedArgs, ...args });
  });

  useEffect(() => {
    latestLoad(memoizedArgs);
  }, [latestLoad, memoizedArgs]);

  const willLoad = useWillLoad(memoizedArgs);

  const effectiveState = willLoad ? {
    // Make autoloader appear as loading even before `load` function
    // is called to avoid unnecessary DOM tree dumping
    stage: LoadingStage.Loading,
    error: undefined,
    value: undefined,
  } as const : loadingState;

  const methods = {
    reload,
    clear: latestClear,
  };

  return {
    ...methods,
    ...effectiveState,
  };
}

/**
 * Returns true if the load function will be called as a result of the current
 * render. This is used to make sure we instantly switch to the loading state
 * when we're about to start loading again (so we don't try to render old
 * components with new args, which can cause components to throw errors).
 */
function useWillLoad(memoizedArgs: unknown): boolean {
  const isFirstRender = useRef(true);
  useEffect(() => { isFirstRender.current = false; }, []);
  const committedArgs = useLatest(memoizedArgs).current;

  // The load function will be called after the first render, and when the args
  // have changed.
  return isFirstRender.current || committedArgs !== memoizedArgs;
}

export interface IPropsNoParamsNoError<LoadType extends LoadFunctionNoParamsType>
extends IAutoLoaderNoParams<LoadType> {
  children: (autoLoader: Exclude<LoadingStateCompat<LoadValueType<LoadType>>, ILoadingStateCompatError> & {
    reload: () => void;
    clearResult: () => void;
  }) => ReactNode;
}

export interface IPropsNoError<LoadType extends LoadFunctionType>
extends IAutoLoaderParams<LoadType> {
  children: (autoLoader: Exclude<LoadingStateCompat<LoadValueType<LoadType>>, ILoadingStateCompatError> & {
    reload: () => void;
    clearResult: () => void;
  }) => ReactNode;
}

interface IProps<LoadType extends LoadFunctionType> extends IAutoLoaderParams<LoadType> {
  children: (autoLoader: LoadingStateCompat<LoadValueType<LoadType>> & {
    reload: () => void;
    clearResult: () => void;
  }) => ReactNode;
}

interface IPropsNoParams<LoadValueNoParamsType extends LoadFunctionNoParamsType>
extends IAutoLoaderNoParams<LoadValueNoParamsType> {
  children: (autoLoader: LoadingStateCompat<LoadValueType<LoadValueNoParamsType>> & {
    reload: () => void;
    clearResult: () => void;
  }) => ReactNode;
}

/**
 * @deprecated Use `useAutoLoader()` hook instead
 */
export default function AutoLoader<LoadValueNoParamsType extends LoadFunctionNoParamsType>
({ children, ...params }: IPropsNoParams<LoadValueNoParamsType>): ReactElement;

export default function AutoLoader<LoadType extends LoadFunctionType>
({ children, ...params }: IProps<LoadType>): ReactElement;

export default function AutoLoader({ children, ...params }:
IProps<LoadFunctionType>): ReactElement {
  const { reload, clear: clearResult, ...autoLoaderStates } = useAutoLoader(params);

  return children({ reload, clearResult, ...compat(autoLoaderStates) }) as ReactElement;
}
